import React, { useState, useEffect, useRef } from 'react';
import {
  User,
  Upload,
  Trash2,
  Save,
  Loader2,
  Shield,
  Key,
  Eye,
  EyeOff,
  Smartphone,
  LogOut,
  Download,
  AlertCircle,
  Check,
  Calendar,
  Clock,
  Globe,
  Hash,
  Palette,
  Languages
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useCompany } from '../../../../contexts/CompanyContext';
import { usePermissions } from '../../../../contexts/PermissionContext';
import { useUserPreferences } from '../../../../contexts/UserPreferencesContext';
import { settingsService } from '../../../../services/settingsService';

const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)' }
];

const TIME_FORMATS = [
  { value: '12h', label: '12-hour (2:30 PM)' },
  { value: '24h', label: '24-hour (14:30)' }
];

const FIRST_DAY_OPTIONS = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' }
];

const NUMBER_FORMATS = [
  { value: 'en-AU', label: 'Australian (1,234.56)' },
  { value: 'en-US', label: 'US (1,234.56)' },
  { value: 'de-DE', label: 'German (1.234,56)' },
  { value: 'fr-FR', label: 'French (1 234,56)' }
];

// Inline error/success alert component for sections
const SectionAlert = ({ type, message, onDismiss }) => {
  if (!message) return null;

  const isError = type === 'error';
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg mb-4 ${
      isError ? 'bg-red-500/10 border border-red-500/20' : 'bg-green-500/10 border border-green-500/20'
    }`}>
      <div className="flex items-center space-x-2">
        {isError ? (
          <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
        ) : (
          <Check size={16} className="text-green-500 flex-shrink-0" />
        )}
        <span className={`text-sm ${isError ? 'text-red-400' : 'text-green-400'}`}>{message}</span>
      </div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-text-secondary hover:text-text-primary text-sm">
          &times;
        </button>
      )}
    </div>
  );
};

const SecurityAccountTab = () => {
  const { user, refreshProfile } = useAuth();
  const { currentCompany } = useCompany();
  const { hasPermission, userRole } = usePermissions();
  const { updatePreferences } = useUserPreferences();
  const canExportCompanyData = userRole === 'primary_owner' || userRole === 'owner' || hasPermission('export_data');
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [exportingData, setExportingData] = useState(false);

  // Section-specific alerts
  const [profileAlert, setProfileAlert] = useState({ type: null, message: null });
  const [prefsAlert, setPrefsAlert] = useState({ type: null, message: null });
  const [passwordAlert, setPasswordAlert] = useState({ type: null, message: null });
  const [sessionAlert, setSessionAlert] = useState({ type: null, message: null });
  const [dataAlert, setDataAlert] = useState({ type: null, message: null });

  // Profile state
  const [profile, setProfile] = useState({
    display_name: '',
    phone: '',
    job_title: '',
    bio: '',
    avatar_url: null
  });

  // Display preferences state
  const [displayPrefs, setDisplayPrefs] = useState({
    date_format: 'DD/MM/YYYY',
    time_format: '12h',
    first_day_of_week: 'monday',
    number_format: 'en-AU',
    theme: 'dark',
    language: 'en'
  });

  // Password state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    loadUserProfile();
  }, []);

  // Auto-dismiss success messages after 3 seconds
  useEffect(() => {
    if (profileAlert.type === 'success') {
      const timer = setTimeout(() => setProfileAlert({ type: null, message: null }), 3000);
      return () => clearTimeout(timer);
    }
  }, [profileAlert]);

  useEffect(() => {
    if (prefsAlert.type === 'success') {
      const timer = setTimeout(() => setPrefsAlert({ type: null, message: null }), 3000);
      return () => clearTimeout(timer);
    }
  }, [prefsAlert]);

  useEffect(() => {
    if (passwordAlert.type === 'success') {
      const timer = setTimeout(() => setPasswordAlert({ type: null, message: null }), 3000);
      return () => clearTimeout(timer);
    }
  }, [passwordAlert]);

  useEffect(() => {
    if (sessionAlert.type === 'success') {
      const timer = setTimeout(() => setSessionAlert({ type: null, message: null }), 3000);
      return () => clearTimeout(timer);
    }
  }, [sessionAlert]);

  useEffect(() => {
    if (dataAlert.type === 'success') {
      const timer = setTimeout(() => setDataAlert({ type: null, message: null }), 3000);
      return () => clearTimeout(timer);
    }
  }, [dataAlert]);

  const loadUserProfile = async () => {
    try {
      setLoading(true);

      try {
        const { data } = await settingsService.getUserProfile();
        setProfile({
          display_name: data?.display_name || data?.full_name || '',
          phone: data?.phone || '',
          job_title: data?.job_title || '',
          bio: data?.bio || '',
          avatar_url: data?.avatar_url
        });
        setDisplayPrefs({
          date_format: data?.date_format || 'DD/MM/YYYY',
          time_format: data?.time_format || '12h',
          first_day_of_week: data?.first_day_of_week || 'monday',
          number_format: data?.number_format || 'en-AU',
          theme: data?.theme || 'dark',
          language: data?.language || 'en'
        });
      } catch (e) {
        console.warn('Extended profile columns may not exist:', e.message);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 512000) {
      setProfileAlert({ type: 'error', message: 'Avatar must be less than 500KB' });
      return;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setProfileAlert({ type: 'error', message: 'Avatar must be PNG, JPG, or WebP' });
      return;
    }

    try {
      setUploadingAvatar(true);
      setProfileAlert({ type: null, message: null });
      const { url } = await settingsService.uploadAvatar(file);
      setProfile(prev => ({ ...prev, avatar_url: url }));
      await refreshProfile?.();
      setProfileAlert({ type: 'success', message: 'Avatar uploaded successfully' });
    } catch (err) {
      console.error('Error uploading avatar:', err);
      setProfileAlert({ type: 'error', message: 'Failed to upload avatar' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      setUploadingAvatar(true);
      await settingsService.deleteAvatar();
      setProfile(prev => ({ ...prev, avatar_url: null }));
      await refreshProfile?.();
      setProfileAlert({ type: 'success', message: 'Avatar removed' });
    } catch (err) {
      console.error('Error deleting avatar:', err);
      setProfileAlert({ type: 'error', message: 'Failed to remove avatar' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setProfileAlert({ type: null, message: null });

      await settingsService.updateUserProfile({
        display_name: profile.display_name,
        phone: profile.phone,
        job_title: profile.job_title,
        bio: profile.bio
      });

      setProfileAlert({ type: 'success', message: 'Profile saved successfully' });
    } catch (err) {
      console.error('Error saving profile:', err);
      setProfileAlert({ type: 'error', message: 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDisplayPrefs = async () => {
    try {
      setSaving(true);
      setPrefsAlert({ type: null, message: null });

      await settingsService.updateDisplayPreferences(displayPrefs);

      // Update the global preferences context so changes reflect immediately
      updatePreferences({
        date_format: displayPrefs.date_format,
        time_format: displayPrefs.time_format,
        first_day_of_week: displayPrefs.first_day_of_week,
        number_format: displayPrefs.number_format
      });

      setPrefsAlert({ type: 'success', message: 'Preferences saved successfully' });
    } catch (err) {
      console.error('Error saving preferences:', err);
      setPrefsAlert({ type: 'error', message: 'Failed to save preferences' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordAlert({ type: null, message: null });

    // Validate inputs
    if (!passwordForm.currentPassword) {
      setPasswordAlert({ type: 'error', message: 'Please enter your current password' });
      return;
    }

    if (!passwordForm.newPassword) {
      setPasswordAlert({ type: 'error', message: 'Please enter a new password' });
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordAlert({ type: 'error', message: 'New password must be at least 8 characters' });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordAlert({ type: 'error', message: 'New passwords do not match' });
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordAlert({ type: 'error', message: 'New password must be different from current password' });
      return;
    }

    try {
      setChangingPassword(true);

      await settingsService.changePasswordWithVerification(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );

      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordAlert({ type: 'success', message: 'Password updated successfully' });
    } catch (err) {
      console.error('Error changing password:', err);
      setPasswordAlert({ type: 'error', message: err.message || 'Failed to update password' });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSignOutOthers = async () => {
    try {
      await settingsService.signOutOtherSessions();
      setSessionAlert({ type: 'success', message: 'Signed out of other sessions' });
    } catch (err) {
      console.error('Error signing out:', err);
      setSessionAlert({ type: 'error', message: 'Failed to sign out other sessions' });
    }
  };

  const handleExportMyData = async () => {
    try {
      setExportingData(true);
      const { data } = await settingsService.exportUserData();

      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setDataAlert({ type: 'success', message: 'Data exported successfully' });
    } catch (err) {
      console.error('Error exporting data:', err);
      setDataAlert({ type: 'error', message: 'Failed to export data' });
    } finally {
      setExportingData(false);
    }
  };

  const handleExportCompanyData = async () => {
    try {
      setExportingData(true);
      const { data } = await settingsService.exportCompanyData(currentCompany.id);

      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `company-data-${currentCompany.company_tag}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setDataAlert({ type: 'success', message: 'Company data exported successfully' });
    } catch (err) {
      console.error('Error exporting company data:', err);
      setDataAlert({ type: 'error', message: 'Failed to export company data' });
    } finally {
      setExportingData(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, text: '', color: '' };
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { level: strength, text: 'Weak', color: 'bg-red-500' };
    if (strength <= 3) return { level: strength, text: 'Fair', color: 'bg-yellow-500' };
    if (strength <= 4) return { level: strength, text: 'Good', color: 'bg-green-500' };
    return { level: 5, text: 'Strong', color: 'bg-green-600' };
  };

  const passwordStrength = getPasswordStrength(passwordForm.newPassword);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-text-primary">Security & Account</h2>
        <p className="text-text-secondary text-sm mt-1">
          Manage your profile, preferences, and security settings
        </p>
      </div>

      {/* ==================== MY PROFILE ==================== */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <User size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">My Profile</h3>
        </div>

        {/* Section Alert */}
        <SectionAlert
          type={profileAlert.type}
          message={profileAlert.message}
          onDismiss={() => setProfileAlert({ type: null, message: null })}
        />

        {/* Avatar (LHS) and Role (RHS) */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-6">
          {/* Avatar - Left Side */}
          <div className="flex items-center space-x-6">
            <div className="w-20 h-20 bg-surface rounded-full flex items-center justify-center overflow-hidden border border-border">
              {uploadingAvatar ? (
                <Loader2 size={24} className="text-orange-500 animate-spin" />
              ) : profile.avatar_url ? (
                <img src={profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-bold text-text-secondary">
                  {profile.display_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-surface text-text-primary rounded-lg hover:bg-surface-light transition-colors text-sm">
                  <Upload size={16} className="mr-2" />
                  Upload Photo
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    disabled={uploadingAvatar}
                  />
                </label>
                {profile.avatar_url && (
                  <button
                    onClick={handleDeleteAvatar}
                    disabled={uploadingAvatar}
                    className="p-2 text-text-secondary hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <p className="text-xs text-text-secondary">PNG, JPG, WebP up to 500KB.</p>
            </div>
          </div>

          {/* Role - Right Side */}
          <div className="md:text-right">
            <label className="block text-text-secondary text-sm mb-2">Your Role</label>
            <span className="inline-block px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded-lg text-sm font-medium capitalize">
              {userRole?.replace(/_/g, ' ') || 'Member'}
            </span>
          </div>
        </div>

        {/* Profile Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-text-secondary text-sm mb-2">Display Name</label>
            <input
              type="text"
              value={profile.display_name}
              onChange={(e) => setProfile({ ...profile, display_name: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
              placeholder="Your name"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">Email</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-secondary cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">Phone Number</label>
            <input
              type="tel"
              value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
              placeholder="+61 400 000 000"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">Job Title</label>
            <input
              type="text"
              value={profile.job_title}
              onChange={(e) => setProfile({ ...profile, job_title: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
              placeholder="Senior Project Manager"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-text-secondary text-sm mb-2">Bio</label>
            <textarea
              value={profile.bio}
              onChange={(e) => setProfile({ ...profile, bio: e.target.value.slice(0, 200) })}
              rows={2}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary resize-none"
              placeholder="A brief description about yourself..."
            />
            <p className="text-xs text-text-secondary mt-1">{profile.bio.length}/200 characters</p>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-text-primary rounded-lg text-sm font-medium hover:from-orange-600 hover:to-orange-700 transition-colors disabled:opacity-50 flex items-center"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
            Save Profile
          </button>
        </div>
      </div>

      {/* ==================== DISPLAY PREFERENCES ==================== */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Palette size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Display Preferences</h3>
        </div>

        {/* Section Alert */}
        <SectionAlert
          type={prefsAlert.type}
          message={prefsAlert.message}
          onDismiss={() => setPrefsAlert({ type: null, message: null })}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Calendar size={14} className="inline mr-1" /> Date Format
            </label>
            <select
              value={displayPrefs.date_format}
              onChange={(e) => setDisplayPrefs({ ...displayPrefs, date_format: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
            >
              {DATE_FORMATS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Clock size={14} className="inline mr-1" /> Time Format
            </label>
            <select
              value={displayPrefs.time_format}
              onChange={(e) => setDisplayPrefs({ ...displayPrefs, time_format: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
            >
              {TIME_FORMATS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Calendar size={14} className="inline mr-1" /> First Day of Week
            </label>
            <select
              value={displayPrefs.first_day_of_week}
              onChange={(e) => setDisplayPrefs({ ...displayPrefs, first_day_of_week: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
            >
              {FIRST_DAY_OPTIONS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Hash size={14} className="inline mr-1" /> Number Format
            </label>
            <select
              value={displayPrefs.number_format}
              onChange={(e) => setDisplayPrefs({ ...displayPrefs, number_format: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
            >
              {NUMBER_FORMATS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Globe size={14} className="inline mr-1" /> Theme
            </label>
            <select
              value={displayPrefs.theme}
              onChange={(e) => setDisplayPrefs({ ...displayPrefs, theme: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
            >
              <option value="dark">Dark</option>
              <option value="light" disabled>Light (Coming Soon)</option>
              <option value="system" disabled>System (Coming Soon)</option>
            </select>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Languages size={14} className="inline mr-1" /> Language
            </label>
            <select
              value={displayPrefs.language}
              disabled
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-secondary cursor-not-allowed"
            >
              <option value="en">English</option>
            </select>
            <p className="text-xs text-text-secondary mt-1">Additional languages coming soon</p>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <button
            onClick={handleSaveDisplayPrefs}
            disabled={saving}
            className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-text-primary rounded-lg text-sm font-medium hover:from-orange-600 hover:to-orange-700 transition-colors disabled:opacity-50 flex items-center"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
            Save Preferences
          </button>
        </div>
      </div>

      {/* ==================== CHANGE PASSWORD ==================== */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Key size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Change Password</h3>
        </div>

        {/* Section Alert */}
        <SectionAlert
          type={passwordAlert.type}
          message={passwordAlert.message}
          onDismiss={() => setPasswordAlert({ type: null, message: null })}
        />

        <div className="max-w-md space-y-4">
          {/* Current Password */}
          <div>
            <label className="block text-text-secondary text-sm mb-2">Current Password</label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                className="w-full px-4 py-2 pr-10 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
              >
                {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-text-secondary text-sm mb-2">New Password</label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                className="w-full px-4 py-2 pr-10 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
              >
                {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {passwordForm.newPassword && (
              <div className="mt-2">
                <div className="flex items-center space-x-2">
                  <div className="flex-1 h-1 bg-border rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${passwordStrength.color}`}
                      style={{ width: `${(passwordStrength.level / 5) * 100}%` }}
                    />
                  </div>
                  <span className={`text-xs ${passwordStrength.level <= 2 ? 'text-red-400' : passwordStrength.level <= 3 ? 'text-yellow-400' : 'text-green-400'}`}>
                    {passwordStrength.text}
                  </span>
                </div>
                <p className="text-xs text-text-secondary mt-1">Use 8+ characters with a mix of letters, numbers & symbols</p>
              </div>
            )}
          </div>

          {/* Confirm New Password */}
          <div>
            <label className="block text-text-secondary text-sm mb-2">Confirm New Password</label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                className={`w-full px-4 py-2 pr-10 bg-surface border rounded-lg text-text-primary focus:outline-none focus:border-primary ${
                  passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword
                    ? 'border-red-500'
                    : 'border-border'
                }`}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
              >
                {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
              <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
            )}
          </div>

          <button
            onClick={handleChangePassword}
            disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
            className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-text-primary rounded-lg text-sm font-medium hover:from-orange-600 hover:to-orange-700 transition-colors disabled:opacity-50 flex items-center"
          >
            {changingPassword ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Updating Password...
              </>
            ) : (
              <>
                <Key size={14} className="mr-2" />
                Update Password
              </>
            )}
          </button>
        </div>

        {/* 2FA Placeholder */}
        <div className="mt-6 p-4 bg-background border border-border rounded-lg max-w-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Smartphone size={20} className="text-text-secondary" />
              <div>
                <p className="text-text-primary text-sm font-medium">Two-Factor Authentication</p>
                <p className="text-text-secondary text-xs">Add an extra layer of security</p>
              </div>
            </div>
            <span className="px-3 py-1 bg-border/50 text-text-secondary rounded-full text-xs">Coming Soon</span>
          </div>
        </div>
      </div>

      {/* ==================== SESSIONS ==================== */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Shield size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Sessions & Devices</h3>
        </div>

        {/* Section Alert */}
        <SectionAlert
          type={sessionAlert.type}
          message={sessionAlert.message}
          onDismiss={() => setSessionAlert({ type: null, message: null })}
        />

        <div className="p-4 bg-background border border-border rounded-lg mb-4 max-w-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-primary text-sm font-medium">Current Session</p>
              <p className="text-text-secondary text-xs">This device</p>
            </div>
            <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">Active</span>
          </div>
        </div>

        <button
          onClick={handleSignOutOthers}
          className="px-4 py-2 bg-surface text-text-primary rounded-lg text-sm font-medium hover:bg-surface-light transition-colors flex items-center"
        >
          <LogOut size={14} className="mr-2" />
          Sign Out Other Sessions
        </button>
      </div>

      {/* ==================== DATA & PRIVACY ==================== */}
      <div>
        <div className="flex items-center space-x-2 mb-4">
          <Download size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Data & Privacy</h3>
        </div>

        {/* Section Alert */}
        <SectionAlert
          type={dataAlert.type}
          message={dataAlert.message}
          onDismiss={() => setDataAlert({ type: null, message: null })}
        />

        <div className="space-y-3">
          <button
            onClick={handleExportMyData}
            disabled={exportingData}
            className="w-full md:w-auto px-4 py-2 bg-surface text-text-primary rounded-lg text-sm font-medium hover:bg-surface-light transition-colors flex items-center"
          >
            {exportingData ? <Loader2 size={14} className="animate-spin mr-2" /> : <Download size={14} className="mr-2" />}
            Export My Data
          </button>

          {canExportCompanyData && (
            <button
              onClick={handleExportCompanyData}
              disabled={exportingData}
              className="w-full md:w-auto px-4 py-2 bg-surface text-text-primary rounded-lg text-sm font-medium hover:bg-surface-light transition-colors flex items-center"
            >
              {exportingData ? <Loader2 size={14} className="animate-spin mr-2" /> : <Download size={14} className="mr-2" />}
              Export Company Data
            </button>
          )}

          <p className="text-xs text-text-secondary">
            Export your data in JSON format for GDPR compliance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SecurityAccountTab;
